// API client for admin portal
// Uses Cognito OAuth2 tokens stored by AuthContext

const API_BASE = '/api/v1/admin';

// Token storage key - must match AuthContext
const ACCESS_TOKEN_KEY = 'admin_access_token';

// Get access token for API calls
export function getStoredToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

// API Error class
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Fetch wrapper with auth
async function fetchWithAuth<T>(
  url: string,
  options: RequestInit = {},
  requireAuth = true
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (requireAuth) {
    const token = getStoredToken();
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: response.statusText };
    }
    throw new ApiError(
      errorData.error || errorData.message || 'Request failed',
      response.status,
      errorData
    );
  }

  return response.json();
}

// Generic API helper for authenticated requests
export async function apiGet<T>(endpoint: string): Promise<T> {
  return fetchWithAuth<T>(`${API_BASE}${endpoint}`, { method: 'GET' });
}

export async function apiPost<T>(endpoint: string, data?: unknown): Promise<T> {
  return fetchWithAuth<T>(`${API_BASE}${endpoint}`, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

export async function apiPut<T>(endpoint: string, data?: unknown): Promise<T> {
  return fetchWithAuth<T>(`${API_BASE}${endpoint}`, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

export async function apiDelete<T>(endpoint: string): Promise<T> {
  return fetchWithAuth<T>(`${API_BASE}${endpoint}`, { method: 'DELETE' });
}

// File upload helper
export async function apiUpload<T>(
  endpoint: string,
  file: File,
  additionalData?: Record<string, string>
): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  const token = getStoredToken();
  const headers: HeadersInit = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: response.statusText };
    }
    throw new ApiError(
      errorData.error || errorData.message || 'Upload failed',
      response.status,
      errorData
    );
  }

  return response.json();
}
